import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import {
  FileCheck,
  User,
  MapPin,
  CreditCard,
  Zap,
  Home,
  Upload,
  CheckCircle2,
  AlertCircle,
  ArrowRight,
  X,
  FileText,
  ShieldCheck,
  AlertTriangle,
} from "lucide-react";
import { sellerAPI } from "../API/axios";

interface FileUploadProps {
  id: string;
  label: string;
  icon: React.ElementType;
  accept?: string;
  hint?: string;
  file: File | null;
  onChange: (file: File) => void;
  onClear: () => void;
}

/* ── File upload widget ── */
const FileUpload: React.FC<FileUploadProps> = ({
  id,
  label,
  icon: Icon,
  accept = "*",
  hint,
  file,
  onChange,
  onClear,
}) => (
  <div className="flex flex-col gap-2.5">
    <div className="flex items-center gap-2 text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">
      <Icon size={14} />
      <span>{label}</span>
    </div>
    {file ? (
      <div className="flex items-center gap-3 p-3.5 bg-primary/5 border border-primary/20 rounded-xl group">
        <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center text-primary shadow-sm">
          <FileText size={20} />
        </div>
        <div className="flex flex-col gap-0.5 overflow-hidden">
          <span className="text-sm font-bold text-slate-900 truncate">
            {file.name}
          </span>
          <span className="text-[10px] font-medium text-slate-500">
            {(file.size / 1024).toFixed(1)} KB
          </span>
        </div>
        <button
          type="button"
          className="ml-auto w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-red-500 hover:bg-red-50 transition-all opacity-0 group-hover:opacity-100"
          onClick={onClear}
        >
          <X size={16} />
        </button>
      </div>
    ) : (
      <label
        htmlFor={id}
        className="flex flex-col items-center justify-center gap-3 p-8 border-2 border-dashed border-slate-200 rounded-2xl hover:border-primary hover:bg-primary/5 transition-all cursor-pointer group"
      >
        <div className="w-12 h-12 rounded-full bg-slate-50 flex items-center justify-center text-primary group-hover:scale-110 transition-transform">
          <Upload size={24} />
        </div>
        <div className="text-center">
          <span className="block text-sm font-bold text-slate-900">
            Click to upload
          </span>
          {hint && (
            <span className="block text-[10px] font-medium text-slate-400 mt-1 uppercase tracking-wider">
              {hint}
            </span>
          )}
        </div>
        <input
          id={id}
          type="file"
          accept={accept}
          className="hidden"
          onChange={(e) =>
            e.target.files && e.target.files[0] && onChange(e.target.files[0])
          }
        />
      </label>
    )}
  </div>
);

/* ── KYC Step config ── */
const STEPS = [
  { num: 1, label: "PAN Details" },
  { num: 2, label: "Signature" },
  { num: 3, label: "Electricity Bill" },
  { num: 4, label: "Address & Submit" },
];

interface KYCForm {
  panNumber: string;
  signatureFile: File | null;
  electricityFile: File | null;
  businessAddress: string;
  pincode: string;
  city: string;
  state: string;
}

interface FormErrors {
  [key: string]: string | undefined;
}

const KYCPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [showPopup, setShowPopup] = useState(false);

  const [form, setForm] = useState<KYCForm>({
    panNumber: "",
    signatureFile: null,
    electricityFile: null,
    businessAddress: "",
    pincode: "",
    city: "",
    state: "",
  });

  // Check if PAN exists in localStorage on component mount
  useEffect(() => {
    const sellerPanNumber = localStorage.getItem("sellerPanNumber");
    const sellerStatus = localStorage.getItem("sellerStatus");

    console.log("Checking localStorage - PAN:", sellerPanNumber);
    console.log("Checking localStorage - Status:", sellerStatus);

    // If PAN exists and is not null/undefined/empty string
    if (
      sellerPanNumber &&
      sellerPanNumber !== "null" &&
      sellerPanNumber !== "undefined" &&
      sellerPanNumber.trim() !== ""
    ) {
      setShowPopup(true);
    }
  }, []);

  const set =
    <K extends keyof KYCForm>(field: K) =>
    (val: KYCForm[K]) =>
      setForm((p) => ({ ...p, [field]: val }));

  const setF =
    (field: keyof KYCForm) =>
    (
      e: React.ChangeEvent<
        HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
      >,
    ) =>
      setForm((p) => ({ ...p, [field]: e.target.value }));

  /* ── Validators per step ── */
  const validateStep = (s: number) => {
    const e: FormErrors = {};

    if (s === 1) {
      if (!form.panNumber) {
        e.panNumber = "PAN number is required";
      } else if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(form.panNumber)) {
        e.panNumber = "Invalid PAN format (e.g., ABCDE1234F)";
      }
    }

    if (s === 2) {
      if (!form.signatureFile) e.signatureFile = "Signature image required";
    }

    if (s === 3) {
      if (!form.electricityFile)
        e.electricityFile = "Electricity bill required";
    }

    if (s === 4) {
      if (form.businessAddress.trim()?.length < 10)
        e.businessAddress = "Enter a valid business address";
      if (!/^\d{6}$/.test(form.pincode))
        e.pincode = "Valid 6-digit pincode required";
      if (!form.city.trim()) e.city = "City is required";
      if (!form.state.trim()) e.state = "State is required";
    }

    setErrors(e);
    return Object.keys(e)?.length === 0;
  };

  const nextStep = () => {
    if (validateStep(step)) setStep((s) => s + 1);
  };

  const prevStep = () => {
    setStep((s) => s - 1);
    setErrors({});
  };

  const handleSubmit = async () => {
    if (!validateStep(4)) return;

    setLoading(true);

    try {
      const fullAddress = `${form.businessAddress}, ${form.city}, ${form.state} - ${form.pincode}`;

      // submit basic KYC
      await sellerAPI.submitKYC({
        panNumber: form.panNumber.toUpperCase(),
        businessAddress: fullAddress,
      });

      const uploads = [];

      if (form.electricityFile) {
        const fd = new FormData();
        fd.append("image", form.electricityFile);
        uploads.push(sellerAPI.uploadAddressProof(fd));
      }

      if (form.signatureFile) {
        const fd = new FormData();
        fd.append("image", form.signatureFile);
        uploads.push(sellerAPI.uploadSignature(fd));
      }

      await Promise.all(uploads);

      toast.success("KYC submitted successfully! Under review 🎉");

      // Update localStorage status
      localStorage.setItem("sellerStatus", "PENDING_APPROVAL");

      setSubmitted(true);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "KYC submission failed");
    } finally {
      setLoading(false);
    }
  };

  const handleGoToDashboard = () => {
    navigate("/dashboard");
  };

  /* ── Success screen ── */
  if (submitted) {
    return (
      <div className="pt-24 pb-16 min-h-screen page-root">
        <div className="max-w-3xl mx-auto px-6">
          <div className="glass-card p-10 text-center animate-slide-up">
            <div className="w-20 h-20 bg-emerald-50 rounded-full flex items-center justify-center text-emerald-500 mx-auto mb-6 shadow-xl shadow-emerald-500/10">
              <CheckCircle2 size={48} />
            </div>
            <h2 className="font-display text-3xl font-extrabold text-slate-900 mb-4 tracking-tight">
              KYC Submitted Successfully!
            </h2>
            <p className="text-slate-500 max-w-lg mx-auto leading-relaxed mb-10">
              Our team will review your documents within{" "}
              <strong className="text-slate-900 font-bold">24–48 hours</strong>.
              You will receive an email confirmation once verified.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 p-6 bg-slate-50 rounded-2xl border border-slate-100">
              {[
                "Documents received",
                "Under review (24–48 hrs)",
                "Email confirmation",
                "Full access unlocked",
              ].map((s, i) => (
                <div key={s} className="flex flex-col items-center gap-3">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                      i === 0
                        ? "bg-emerald-500 text-white"
                        : i === 1
                          ? "bg-primary text-white shadow-lg shadow-primary/20"
                          : "bg-slate-200 text-slate-500"
                    }`}
                  >
                    {i + 1}
                  </div>
                  <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest text-center">
                    {s}
                  </span>
                </div>
              ))}
            </div>

            <button
              onClick={handleGoToDashboard}
              className="mt-8 btn btn-primary !px-10"
            >
              Go to Dashboard <ArrowRight size={18} className="ml-2" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-24 pb-16 min-h-screen page-root">
      {/* KYC Already Submitted Popup */}
      {showPopup && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
          <div className="bg-white rounded-2xl max-w-md w-full p-8 shadow-2xl animate-slideUp">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle size={32} className="text-amber-600" />
            </div>

            <h3 className="text-xl font-bold text-center text-gray-900 mb-2">
              KYC Already Submitted
            </h3>

            <p className="text-gray-600 text-center mb-6">
              We have received your KYC information (PAN:{" "}
              {localStorage.getItem("sellerPanNumber")}). Your application is
              currently under review. You can check the status in your
              dashboard.
            </p>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
              <div className="flex items-center gap-3">
                <ShieldCheck size={20} className="text-amber-600" />
                <div>
                  <p className="text-sm font-medium text-amber-800">
                    KYC Status
                  </p>
                  <p className="text-xs text-amber-600 mt-0.5">
                    {localStorage.getItem("sellerStatus") === "PENDING_APPROVAL"
                      ? "Pending Approval"
                      : "Under Review"}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              {/* <button
                onClick={() => setShowPopup(false)}
                className="flex-1 px-4 py-3 border-2 border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 transition-all font-medium"
              >
                Stay Here
              </button> */}
              <button
                onClick={handleGoToDashboard}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:from-green-700 hover:to-emerald-700 transition-all font-medium shadow-lg shadow-green-200"
              >
                Go to Dashboard
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-6">
        {/* ── Header ── */}
        <div className="flex flex-col sm:flex-row items-center gap-6 mb-12 animate-slide-up">
          <div className="w-16 h-16 bg-emerald-50 border border-emerald-200 rounded-2xl flex items-center justify-center text-emerald-500 shadow-xl shadow-emerald-500/10">
            <FileCheck size={28} />
          </div>
          <div className="text-center sm:text-left">
            <h1 className="font-display text-3xl font-extrabold text-slate-900 tracking-tight">
              KYC Verification
            </h1>
            <p className="text-slate-500 text-sm font-medium mt-1">
              Complete all four steps to verify your identity and business
              credentials.
            </p>
          </div>
        </div>

        {/* ── Step bar ── */}
        <div className="step-bar mb-12 animate-slide-up">
          {STEPS.map(({ num, label }, idx) => (
            <React.Fragment key={num}>
              <div className="step-item">
                <div
                  className={`step-num ${
                    step > num ? "done" : step === num ? "active" : "inactive"
                  }`}
                >
                  {step > num ? <CheckCircle2 size={16} /> : num}
                </div>
                <span
                  className={`step-label ${
                    step === num ? "active" : step > num ? "done" : ""
                  }`}
                >
                  {label}
                </span>
              </div>
              {idx < STEPS.length - 1 && (
                <div className={`step-connector ${step > num ? "done" : ""}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* ── Step content ── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 animate-slide-up">
          <div className="lg:col-span-2 inner-card !p-8 flex flex-col gap-8">
            {/* STEP 1 — PAN */}
            {step === 1 && (
              <div className="flex flex-col gap-6 animate-slide-up">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary/5 rounded-xl flex items-center justify-center text-primary">
                    <CreditCard size={20} />
                  </div>
                  <div>
                    <h2 className="text-lg font-extrabold text-slate-900 tracking-tight">
                      PAN Card Details
                    </h2>
                    <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">
                      Tax compliance verification
                    </p>
                  </div>
                </div>

                <div className="flex flex-col gap-6">
                  <div className="flex flex-col gap-2">
                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">
                      PAN Number
                    </label>
                    <input
                      id="kyc-pan"
                      className="form-input !font-mono !tracking-[0.2em] !text-lg !uppercase"
                      placeholder="ABCDE1234F"
                      maxLength={10}
                      value={form.panNumber}
                      onChange={(e) =>
                        set("panNumber")(e.target.value.toUpperCase())
                      }
                    />
                    {errors.panNumber && (
                      <span className="text-[10px] font-medium text-red-500 ml-1">
                        {errors.panNumber}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}
            {/* STEP 2 — signature */}
            {step === 2 && (
              <div className="flex flex-col gap-6 animate-slide-up">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-secondary/5 rounded-xl flex items-center justify-center text-secondary">
                    <User size={20} />
                  </div>
                  <div>
                    <h2 className="text-lg font-extrabold text-slate-900 tracking-tight">
                      signature Details
                    </h2>
                    <p className="text-xs font-medium text-slate-400 uppercase tracking-wider"></p>
                  </div>
                  Digital signature
                </div>

                <div className="flex flex-col gap-6">
                  <FileUpload
                    id="signature"
                    label="Upload signature(must use white paper)"
                    icon={Upload}
                    accept="image/*,.pdf"
                    hint="JPG, PNG or PDF · Max 5 MB"
                    file={form.signatureFile}
                    onChange={set("signatureFile")}
                    onClear={() => set("signatureFile")(null)}
                  />
                  {errors.signatureFile && (
                    <span className="text-[10px] font-medium text-red-500 ml-1">
                      {errors.signatureFile}
                    </span>
                  )}

                  <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-100 rounded-xl text-amber-700">
                    <AlertCircle size={16} className="shrink-0 mt-0.5" />
                    <span className="text-xs font-medium leading-relaxed">
                      Make sure all four corners and text are clearly visible.
                      Blurry images will be rejected.
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* STEP 3 — ELECTRICITY BILL */}
            {step === 3 && (
              <div className="flex flex-col gap-6 animate-slide-up">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-amber-50 rounded-xl flex items-center justify-center text-amber-500">
                    <Zap size={20} />
                  </div>
                  <div>
                    <h2 className="text-lg font-extrabold text-slate-900 tracking-tight">
                      Electricity Bill
                    </h2>
                    <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">
                      Business address verification
                    </p>
                  </div>
                </div>

                <div className="flex flex-col gap-6">
                  <FileUpload
                    id="kyc-elec-file"
                    label="Upload Electricity Bill"
                    icon={Upload}
                    accept="image/*,.pdf"
                    hint="JPG, PNG or PDF · Max 10 MB · Within 3 months"
                    file={form.electricityFile}
                    onChange={set("electricityFile")}
                    onClear={() => set("electricityFile")(null)}
                  />
                  {errors.electricityFile && (
                    <span className="text-[10px] font-medium text-red-500 ml-1">
                      {errors.electricityFile}
                    </span>
                  )}

                  <div className="flex items-start gap-3 p-4 bg-primary/5 border border-primary/10 rounded-xl text-primary">
                    <AlertCircle size={16} className="shrink-0 mt-0.5" />
                    <span className="text-xs font-medium leading-relaxed">
                      The name and address on the bill must match your
                      registered business address.
                    </span>
                  </div>

                  <div className="flex flex-col gap-3">
                    <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">
                      Also accepted:
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {[
                        "Water Bill",
                        "Gas Bill",
                        "Broadband Bill",
                        "Property Tax Receipt",
                      ].map((t) => (
                        <span
                          key={t}
                          className="px-3 py-1 bg-slate-100 border border-slate-200 rounded-full text-[10px] font-bold text-slate-500"
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* STEP 4 — ADDRESS */}
            {step === 4 && (
              <div className="flex flex-col gap-6 animate-slide-up">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center text-emerald-500">
                    <MapPin size={20} />
                  </div>
                  <div>
                    <h2 className="text-lg font-extrabold text-slate-900 tracking-tight">
                      Business Address
                    </h2>
                    <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">
                      Official billing address
                    </p>
                  </div>
                </div>

                <div className="flex flex-col gap-5">
                  <div className="flex flex-col gap-2">
                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">
                      Street Address / Shop No. / Floor
                    </label>
                    <div className="relative group">
                      <Home
                        size={16}
                        className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-primary transition-colors"
                      />
                      <input
                        id="kyc-address"
                        className="form-input !pl-11"
                        placeholder="Shop 12, 3rd Floor, ABC Complex, MG Road"
                        value={form.businessAddress}
                        onChange={setF("businessAddress")}
                      />
                    </div>
                    {errors.businessAddress && (
                      <span className="text-[10px] font-medium text-red-500 ml-1">
                        {errors.businessAddress}
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div className="flex flex-col gap-2">
                      <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">
                        City
                      </label>
                      <input
                        id="kyc-city"
                        className="form-input"
                        placeholder="Mumbai"
                        value={form.city}
                        onChange={setF("city")}
                      />
                      {errors.city && (
                        <span className="text-[10px] font-medium text-red-500 ml-1">
                          {errors.city}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">
                        Pincode
                      </label>
                      <input
                        id="kyc-pincode"
                        className="form-input"
                        placeholder="400001"
                        maxLength={6}
                        inputMode="numeric"
                        value={form.pincode}
                        onChange={(e) =>
                          set("pincode")(
                            e.target.value.replace(/\D/g, "").slice(0, 6),
                          )
                        }
                      />
                      {errors.pincode && (
                        <span className="text-[10px] font-medium text-red-500 ml-1">
                          {errors.pincode}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col gap-2">
                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">
                      State
                    </label>
                    <select
                      id="kyc-state"
                      className="form-input"
                      value={form.state}
                      onChange={setF("state")}
                    >
                      <option value="">Select State</option>
                      {[
                        "Andhra Pradesh",
                        "Arunachal Pradesh",
                        "Assam",
                        "Bihar",
                        "Chhattisgarh",
                        "Goa",
                        "Gujarat",
                        "Haryana",
                        "Himachal Pradesh",
                        "Jharkhand",
                        "Karnataka",
                        "Kerala",
                        "Madhya Pradesh",
                        "Maharashtra",
                        "Manipur",
                        "Meghalaya",
                        "Mizoram",
                        "Nagaland",
                        "Odisha",
                        "Punjab",
                        "Rajasthan",
                        "Sikkim",
                        "Tamil Nadu",
                        "Telangana",
                        "Tripura",
                        "Uttar Pradesh",
                        "Uttarakhand",
                        "West Bengal",
                        "Delhi",
                        "Jammu & Kashmir",
                        "Ladakh",
                        "Puducherry",
                        "Chandigarh",
                      ].map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                    {errors.state && (
                      <span className="text-[10px] font-medium text-red-500 ml-1">
                        {errors.state}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* ── Navigation buttons ── */}
            <div className="flex flex-wrap items-center gap-4 mt-auto pt-8 border-t border-slate-50">
              {step > 1 && (
                <button
                  type="button"
                  className="btn btn-ghost !px-8"
                  onClick={prevStep}
                >
                  ← Back
                </button>
              )}
              <div className="grow" />
              {step < 4 ? (
                <button
                  id={`kyc-next-${step}`}
                  type="button"
                  className="btn btn-primary !px-10"
                  onClick={nextStep}
                >
                  Next Step <ArrowRight size={18} className="ml-1" />
                </button>
              ) : (
                <button
                  id="kyc-submit"
                  type="button"
                  className="btn btn-primary btn-lg !px-10 shadow-xl shadow-primary/20"
                  onClick={handleSubmit}
                  disabled={loading}
                >
                  {loading ? (
                    <div className="flex items-center gap-3">
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      <span>Submitting...</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <CheckCircle2 size={20} />
                      <span>Submit KYC</span>
                    </div>
                  )}
                </button>
              )}
            </div>
          </div>

          {/* ── Side checklist ── */}
          <div className="flex flex-col gap-6">
            <div className="inner-card">
              <h3 className="text-sm font-bold text-slate-900 mb-6 flex items-center gap-2">
                <CheckCircle2 size={16} className="text-primary" />
                KYC Checklist
              </h3>
              <div className="flex flex-col gap-5">
                {STEPS.map(({ num, label }) => (
                  <div key={num} className="flex items-center gap-4 group">
                    <div
                      className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold border transition-all ${
                        step > num
                          ? "bg-emerald-500 border-emerald-500 text-white"
                          : step === num
                            ? "bg-primary border-primary text-white shadow-lg shadow-primary/20"
                            : "bg-slate-50 border-slate-200 text-slate-400 group-hover:border-primary/20"
                      }`}
                    >
                      {step > num ? <CheckCircle2 size={14} /> : num}
                    </div>
                    <span
                      className={`text-xs font-bold transition-all ${
                        step === num
                          ? "text-primary"
                          : step > num
                            ? "text-emerald-600"
                            : "text-slate-400"
                      }`}
                    >
                      {label}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="inner-card bg-slate-50 border-none">
              <h3 className="text-sm font-bold text-slate-900 mb-4 flex items-center gap-2">
                Document Tips
              </h3>
              <div className="flex flex-col gap-4">
                {[
                  "Use high-quality images (min 300 DPI)",
                  "Ensure no glare or shadows on documents",
                  "All text must be clearly legible",
                  "Documents should not be expired",
                  "PDFs accepted for all document types",
                ].map((tip) => (
                  <div key={tip} className="flex items-start gap-3 group">
                    <div className="w-5 h-5 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-500 shrink-0 mt-0.5">
                      <CheckCircle2 size={12} />
                    </div>
                    <span className="text-xs font-medium text-slate-500 leading-relaxed group-hover:text-slate-900 transition-colors">
                      {tip}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KYCPage;
